import React, { useEffect, useState } from 'react';
import { FileText, Receipt, Plus, X, Trash, SpinnerGap, PaperPlaneTilt } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import SignerPicker from './SignerPicker';
import { documentsAPI } from '../api/api';
import { toast } from '../hooks/use-toast';

const DEFAULT_CONTRACT_BODY = `SCOPE OF WORK
Provide a clear description of the work to be performed, including materials, deliverables, and key milestones.

PAYMENT TERMS
Total contract amount, payment schedule, and any deposit requirements.

TIMELINE
Start date, key milestone dates, and completion date.

WARRANTIES & GUARANTEES
Standard warranties on materials and workmanship.

DISPUTE RESOLUTION
How disagreements will be resolved (mediation/arbitration).

By signing, both parties agree to the terms above.`;

const DEFAULT_CHANGE_ORDER_BODY = `CHANGE SUMMARY
Briefly describe what is changing from the original contract.

REASON FOR CHANGE
Explain why the change is necessary.

REVISED SCOPE
Detail the new scope of work, if applicable.

REVISED PRICE
List any cost increase/decrease and the new contract total.

REVISED TIMELINE
List any schedule impact and the new completion date.

By signing, both parties agree this change order amends the original contract.`;

/**
 * CreateDocumentModal
 * Props:
 *  - open / onOpenChange
 *  - jobId (required)
 *  - documentType: 'contract' | 'change_order'
 *  - defaultSigners?: array of { user_id, role, name, email } prefilled (e.g. job participants)
 *  - parentDocumentId?: required when documentType === 'change_order'
 *  - onCreated?(doc): callback after success
 */
export default function CreateDocumentModal({
  open, onOpenChange, jobId, documentType = 'contract',
  defaultSigners = [], parentDocumentId = null, onCreated
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [signers, setSigners] = useState([]);
  const [amount, setAmount] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle('');
      setAmount('');
      setChangeSummary('');
      setBody(documentType === 'change_order' ? DEFAULT_CHANGE_ORDER_BODY : DEFAULT_CONTRACT_BODY);
      setSigners(defaultSigners.length ? [...defaultSigners] : [
        { user_id: '', role: 'contractor', name: '', email: '' },
        { user_id: '', role: 'worker', name: '', email: '' },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-only reset
  }, [open, documentType]);

  const addSigner = () => {
    if (signers.length >= 5) return;
    setSigners([...signers, { user_id: '', role: 'homeowner', name: '', email: '' }]);
  };
  const removeSigner = (idx) => {
    setSigners(signers.filter((_, i) => i !== idx));
  };
  const updateSigner = (idx, patch) => {
    setSigners(signers.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: 'Add a document title', variant: 'destructive' });
      return;
    }
    if (!body.trim()) {
      toast({ title: 'Add document content', variant: 'destructive' });
      return;
    }
    const cleanSigners = signers.filter(s => s.user_id && s.email && s.name);
    if (cleanSigners.length === 0) {
      toast({ title: 'Add at least one signer with a valid user ID', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const metadata = {};
      if (amount) metadata.amount = parseFloat(amount);
      if (changeSummary && documentType === 'change_order') metadata.change_summary = changeSummary;

      const payload = {
        document_type: documentType,
        job_id: jobId,
        title: title.trim(),
        body: body.trim(),
        signers: cleanSigners.map((s, i) => ({
          user_id: s.user_id,
          role: s.role,
          name: s.name,
          email: s.email,
          order: i,
        })),
        metadata,
      };
      if (documentType === 'change_order' && parentDocumentId) {
        payload.parent_document_id = parentDocumentId;
      }

      const res = await documentsAPI.create(payload);
      toast({ title: 'Document created', description: 'Signers have been notified.' });
      onOpenChange(false);
      onCreated?.(res.data.document);
    } catch (err) {
      toast({ title: 'Could not create document', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = documentType === 'change_order' ? Receipt : FileText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon size={20} weight="bold" className="text-[#2B4C6F]" />
            New {documentType === 'change_order' ? 'Change Order' : 'Contract'}
          </DialogTitle>
          <DialogDescription>
            Draft the document, list everyone who needs to sign, and we&apos;ll notify them. You can edit until anyone signs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="doc-title">Title</Label>
            <Input
              id="doc-title"
              placeholder={documentType === 'change_order' ? 'e.g. Change Order #1 — Added flooring' : 'e.g. Master Service Agreement'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              data-testid="doc-title-input"
            />
          </div>

          <div>
            <Label htmlFor="doc-body">Body</Label>
            <Textarea
              id="doc-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="mt-1 font-mono text-sm"
              data-testid="doc-body-input"
            />
            <p className="text-[10px] text-neutral-500 mt-1">Markdown supported. Be specific about scope, price, timeline, and warranties.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="doc-amount">Total amount (optional)</Label>
              <Input
                id="doc-amount"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1"
                data-testid="doc-amount-input"
              />
            </div>
            {documentType === 'change_order' && (
              <div>
                <Label htmlFor="doc-change-summary">Change summary (1 line)</Label>
                <Input
                  id="doc-change-summary"
                  placeholder="What changed?"
                  value={changeSummary}
                  onChange={(e) => setChangeSummary(e.target.value)}
                  className="mt-1"
                  data-testid="doc-change-summary-input"
                />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Signers ({signers.length})</Label>
              <Button
                type="button"
                onClick={addSigner}
                disabled={signers.length >= 5}
                variant="outline"
                size="sm"
                className="border-2 border-black font-bold"
                data-testid="add-signer-btn"
              >
                <Plus size={14} weight="bold" className="mr-1" />
                Add signer
              </Button>
            </div>
            <div className="space-y-2" data-testid="signers-fields">
              {signers.map((s, idx) => {
                const otherIds = signers.filter((_, i) => i !== idx).map(o => o.user_id).filter(Boolean);
                return (
                  <div key={`signer-${idx}`} className="bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-9 sm:col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider">Role</Label>
                        <select
                          value={s.role}
                          onChange={(e) => updateSigner(idx, { role: e.target.value })}
                          className="mt-1 h-10 w-full px-2 border border-neutral-300 rounded text-sm bg-white"
                          data-testid={`signer-role-${idx}`}
                        >
                          <option value="contractor">Contractor</option>
                          <option value="worker">Worker</option>
                          <option value="homeowner">Homeowner</option>
                        </select>
                      </div>
                      <div className="col-span-3 sm:col-span-1 flex sm:justify-end">
                        {signers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSigner(idx)}
                            className="h-10 w-10 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded border border-rose-300"
                            data-testid={`remove-signer-${idx}`}
                            aria-label="Remove signer row"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                      <div className="col-span-12 sm:col-span-8">
                        <Label className="text-[10px] uppercase tracking-wider">Pick a user</Label>
                        <div className="mt-1">
                          <SignerPicker
                            value={s.user_id ? { user_id: s.user_id, name: s.name, email: s.email, role: s.role } : null}
                            onChange={(picked) => {
                              if (picked) {
                                updateSigner(idx, {
                                  user_id: picked.user_id,
                                  name: picked.name,
                                  email: picked.email,
                                  role: picked.role || s.role,
                                });
                              } else {
                                updateSigner(idx, { user_id: '', name: '', email: '' });
                              }
                            }}
                            role={s.role}
                            excludeUserIds={otherIds}
                            testidPrefix={`signer-picker-${idx}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            <X size={16} weight="bold" className="mr-1" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#2B4C6F] hover:bg-[#4a7ba0] text-white border-2 border-black font-bold shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000]"
            data-testid="create-doc-submit"
          >
            {submitting
              ? <SpinnerGap size={18} weight="bold" className="mr-2 animate-spin" />
              : <PaperPlaneTilt size={16} weight="fill" className="mr-2" />
            }
            Send for Signatures
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
